/**
 * Reusable Add/Edit account modal. In "edit" mode (an `account` prop is
 * supplied) it prefills the form and dispatches updateAccount; otherwise it
 * creates a new account.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { RootState, AppDispatch } from '../../store';
import { createAccount, updateAccount, clearAccountsError } from '../../store/accountsSlice';
import { Account } from '../../types/account';

interface AccountFormModalProps {
    show: boolean;
    onHide: () => void;
    account?: Account | null;
}

const AccountFormModal: React.FC<AccountFormModalProps> = ({ show, onHide, account }) => {
    const isEdit = !!account;
    const dispatch = useDispatch<AppDispatch>();
    const error = useSelector((state: RootState) => state.accounts.error);

    const [accountName, setAccountName] = useState('');
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Sync the form with the account being edited whenever the modal opens.
    useEffect(() => {
        if (show) {
            setAccountName(account?.account_name ?? '');
            setValidated(false);
            dispatch(clearAccountsError());
        }
    }, [show, account, dispatch]);

    const handleClose = () => {
        dispatch(clearAccountsError());
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }
        setValidated(true);
        setSubmitting(true);
        try {
            if (isEdit && account) {
                await dispatch(
                    updateAccount({
                        accountId: account.id,
                        data: { account_name: accountName.trim() },
                    })
                ).unwrap();
            } else {
                await dispatch(
                    createAccount({ account_name: accountName.trim() })
                ).unwrap();
            }
            handleClose();
        } catch {
            // Error surfaced via the redux `error` state and the Alert below.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Edit Account' : 'Create New Account'}</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => dispatch(clearAccountsError())}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form.Group className="mb-3" controlId="accountName">
                        <Form.Label>Account Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter account name"
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            required
                            minLength={1}
                            maxLength={255}
                            disabled={submitting}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter an account name (1-255 characters).
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {isEdit ? 'Saving...' : 'Creating...'}
                            </>
                        ) : isEdit ? (
                            'Save Changes'
                        ) : (
                            'Create Account'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AccountFormModal;
